import { useState, useEffect, useCallback } from 'react';
import {
  Trophy, Star, Zap, TrendingUp, Award, Users, Target,
  Flame, Crown, Medal, Globe, MapPin, Shield, Coins,
  Loader2, RefreshCw, ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  getGamificationWallet,
  getGamificationLeaderboard,
  getMentorXP,
  convertStnxpToGnc,
} from '@/api/stnApi';

// ── Icon map (badge icons are stored as strings in config) ────────────────────
const ICON_MAP = {
  Trophy, Star, Zap, TrendingUp, Award, Users, Target,
  Flame, Crown, Medal, Globe, MapPin, Shield,
};
const getIcon = (name) => ICON_MAP[name] || Trophy;

// ── Rank badge colors ─────────────────────────────────────────────────────────
const rankColor = (rank) =>
  rank === 1 ? 'bg-warning/15 text-warning' :
  rank === 2 ? 'bg-muted-foreground/15 text-muted-foreground' :
  rank === 3 ? 'bg-chart-4/15 text-chart-4' :
  'bg-muted text-muted-foreground';

// ── Loading skeleton ──────────────────────────────────────────────────────────
function Skeleton({ className }) {
  return <div className={cn('animate-pulse bg-muted rounded', className)} />;
}

// ── XP progress header ────────────────────────────────────────────────────────
function XPHeader({ wallet, startup }) {
  const pct = wallet.xp_to_next
    ? Math.min(100, ((wallet.lifetime_stnxp - (wallet.xp_prev_level || 0)) /
        (wallet.xp_to_next - (wallet.xp_prev_level || 0))) * 100)
    : 100;

  return (
    <Card className="border-primary/20 bg-primary/5 overflow-hidden relative">
      <div className="absolute top-0 right-0 w-48 h-48 opacity-5"
        style={{ background: 'radial-gradient(circle, hsl(var(--primary)), transparent)' }} />
      <CardContent className="p-6">
        <div className="flex items-start gap-6">
          <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center flex-shrink-0">
            <Trophy className="w-8 h-8 text-primary-foreground" />
          </div>
          <div className="flex-1">
            <div className="flex items-center flex-wrap gap-3 mb-1">
              <h2 className="text-xl font-bold text-foreground">{startup?.companyName || 'Your Company'}</h2>
              <span className="badge-primary">Level {wallet.level}</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {wallet.lifetime_stnxp.toLocaleString()} STNXP • {wallet.gnc_balance.toLocaleString()} GNC
            </p>
            <div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-muted-foreground">Progress to Level {wallet.level + 1}</span>
                <span className="text-foreground font-medium">
                  {wallet.lifetime_stnxp.toLocaleString()} / {wallet.xp_to_next?.toLocaleString() ?? '∞'} XP
                </span>
              </div>
              <Progress value={pct} className="h-2" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
function OverviewTab({ wallet, badges, converting, onConvert }) {
  const earnedBadges = badges.filter(b => b.earned);

  const stats = [
    { label: 'STNXP Balance',  value: wallet.stnxp_balance.toLocaleString(), icon: Zap,    color: 'text-primary',   bg: 'bg-primary/10'  },
    { label: 'Badges Earned',  value: `${earnedBadges.length}/${badges.length}`, icon: Award,  color: 'text-chart-4',   bg: 'bg-chart-4/10'  },
    { label: 'Total GNC',      value: wallet.gnc_balance.toLocaleString(),    icon: Coins,  color: 'text-warning',   bg: 'bg-warning/10'  },
    { label: 'Lifetime STNXP', value: wallet.lifetime_stnxp.toLocaleString(), icon: Crown,  color: 'text-chart-5',   bg: 'bg-chart-5/10'  },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(stat => (
          <Card key={stat.label} className="border-border">
            <CardContent className="p-4">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${stat.bg}`}>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-xl font-bold text-foreground">{stat.value}</div>
              <div className="text-xs text-muted-foreground">{stat.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Convert STNXP → GNC */}
      {wallet.stnxp_balance >= (wallet.config?.stnxp_to_gnc_rate || 100) && (
        <Card className="border-warning/20 bg-warning/5">
          <CardContent className="p-4 flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-foreground text-sm">Convert STNXP to GNC</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Rate: {wallet.config?.stnxp_to_gnc_rate || 100} STNXP = 1 GNC •{' '}
                You can get {Math.floor(wallet.stnxp_balance / (wallet.config?.stnxp_to_gnc_rate || 100))} GNC now
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={onConvert} disabled={converting}
              className="border-warning text-warning hover:bg-warning/10 shrink-0">
              {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4 mr-1.5" />}
              Convert
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Earned badges preview */}
      {earnedBadges.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Your Badges</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {earnedBadges.map(badge => {
              const Icon = getIcon(badge.icon);
              return (
                <div key={badge.id} className={cn('p-4 rounded-xl border border-border flex flex-col items-center text-center gap-2', badge.bg)}>
                  <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', badge.bg)}>
                    <Icon className={cn('w-5 h-5', badge.color)} />
                  </div>
                  <span className="text-xs font-medium text-foreground">{badge.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent ledger */}
      {wallet.ledger?.length > 0 && (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Recent STNXP Activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {wallet.ledger.slice(0, 8).map((entry, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                <div>
                  <p className="text-xs font-medium text-foreground capitalize">
                    {entry.event.replace(/_/g, ' ')}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(entry.earned_at).toLocaleDateString()}
                  </p>
                </div>
                <span className="text-sm font-bold text-primary">+{entry.stnxp} XP</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Badges Tab ────────────────────────────────────────────────────────────────
function BadgesTab({ badges }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {badges.map(badge => {
        const Icon = getIcon(badge.icon);
        return (
          <Card key={badge.id} className={cn('border', badge.earned ? 'border-border' : 'border-dashed border-border opacity-60')}>
            <CardContent className="p-5 text-center">
              <div className={cn('w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3', badge.bg)}>
                <Icon className={cn('w-6 h-6', badge.earned ? badge.color : 'text-muted-foreground')} />
              </div>
              <div className="font-semibold text-sm text-foreground mb-1">{badge.name}</div>
              <div className="text-xs text-muted-foreground mb-2">{badge.desc}</div>
              {badge.earned ? (
                <span className="badge-success">Earned</span>
              ) : (
                <span className="badge-muted">Locked</span>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ── Leaderboard Tab ───────────────────────────────────────────────────────────
function LeaderboardTab({ leaderboard, myEntry }) {
  return (
    <div className="space-y-4">
      {myEntry && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center font-bold text-primary text-sm">
              #{myEntry.rank}
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">Your Global Rank</p>
              <p className="text-xs text-muted-foreground">Lv {myEntry.level} • {myEntry.stnxp.toLocaleString()} STNXP</p>
            </div>
            <span className="badge-primary">You</span>
          </CardContent>
        </Card>
      )}

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary" /> Global Startup Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {leaderboard.map(entry => (
            <div key={entry.startup_id}
              className={cn(
                'flex items-center gap-4 p-3 rounded-xl transition-colors',
                entry.isYou ? 'bg-primary/8 border border-primary/15' : 'hover:bg-muted/50'
              )}
            >
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0', rankColor(entry.rank))}>
                {entry.rank === 1 ? <Crown className="w-4 h-4" /> : entry.rank}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={cn('font-medium text-sm', entry.isYou ? 'text-primary' : 'text-foreground')}>
                    {entry.companyName}
                  </span>
                  {entry.isYou && <span className="badge-primary text-xs">You</span>}
                </div>
                <span className="text-xs text-muted-foreground">{entry.location || entry.sector || '—'}</span>
              </div>
              <div className="text-right">
                <div className="font-bold text-sm text-foreground">{entry.stnxp.toLocaleString()} XP</div>
                <div className="text-xs text-muted-foreground">Lv {entry.level} • {entry.gnc} GNC</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Milestones Tab ────────────────────────────────────────────────────────────
function MilestonesTab({ milestones }) {
  return (
    <div className="space-y-3">
      {milestones.map(m => (
        <Card key={m.id} className={cn('border', m.completed ? 'border-success/20 bg-success/5' : 'border-border')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn('w-8 h-8 rounded-full flex items-center justify-center', m.completed ? 'bg-success/15' : 'bg-muted')}>
                  <Target className={cn('w-4 h-4', m.completed ? 'text-success' : 'text-muted-foreground')} />
                </div>
                <div>
                  <span className="font-medium text-sm text-foreground">{m.label}</span>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <Zap className="w-3 h-3" /> +{m.stnxp} STNXP reward
                  </div>
                </div>
              </div>
              {m.completed
                ? <span className="badge-success">Completed</span>
                : <span className="badge-muted">In Progress</span>
              }
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Mentors XP Tab ────────────────────────────────────────────────────────────
function MentorsTab({ mentors, totalMnxp }) {
  return (
    <div className="space-y-4">
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Zap className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">{totalMnxp.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Total MNXP across your mentor team</p>
          </div>
        </CardContent>
      </Card>

      {mentors.length === 0 ? (
        <Card className="border-dashed border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            No verified mentors yet. Invite and onboard mentors to see their MNXP here.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {mentors.map((m, i) => (
            <Card key={m.profile_id} className="border-border">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-chart-1/10 flex items-center justify-center text-xs font-bold text-chart-1 flex-shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">{m.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {Array.isArray(m.expertise) ? m.expertise.slice(0, 3).join(', ') : m.status}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold text-sm text-foreground">{m.mnxp_balance.toLocaleString()} MNXP</div>
                  <div className="text-xs text-muted-foreground">{m.gnc_balance} GNC</div>
                </div>
                <div className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0',
                  m.status === 'Active' ? 'bg-success' : 'bg-muted-foreground/40')} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Gamification() {
  const [tab, setTab]               = useState('overview');
  const [walletData, setWalletData] = useState(null);
  const [lbData, setLbData]         = useState({ leaderboard: [], myEntry: null });
  const [mentorData, setMentorData] = useState({ mentors: [], totalMnxp: 0 });
  const [loading, setLoading]       = useState(true);
  const [converting, setConverting] = useState(false);
  const [error, setError]           = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [wallet, lb, mentors] = await Promise.all([
        getGamificationWallet(),
        getGamificationLeaderboard({ limit: 15 }),
        getMentorXP(),
      ]);
      setWalletData(wallet);
      setLbData(lb);
      setMentorData(mentors);
    } catch (e) {
      setError(e.message || 'Failed to load gamification data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConvert = async () => {
    setConverting(true);
    try {
      const r = await convertStnxpToGnc();
      // Show simple alert (no toast dep) — or reload
      await load();
      alert(r?.message || 'Conversion successful!');
    } catch (e) {
      alert(e.message || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  if (loading) return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-32 w-full rounded-2xl" />
      <div className="grid grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
    </div>
  );

  if (error) return (
    <div className="p-6 flex flex-col items-center justify-center gap-4 min-h-64">
      <p className="text-muted-foreground text-sm">{error}</p>
      <Button size="sm" variant="outline" onClick={load}>
        <RefreshCw className="w-4 h-4 mr-2" /> Retry
      </Button>
    </div>
  );

  const { wallet, badges = [], milestones = [], startup } = walletData || {};

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Gamification & Recognition</h1>
          <p className="text-muted-foreground text-sm mt-1">STNXP, GNC, badges, milestones, and startup leaderboard</p>
        </div>
        <Button size="sm" variant="ghost" onClick={load} className="text-muted-foreground">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {wallet && <XPHeader wallet={wallet} startup={startup} />}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="badges">Badges ({badges.filter(b => b.earned).length}/{badges.length})</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
          <TabsTrigger value="milestones">Milestones</TabsTrigger>
          <TabsTrigger value="mentors">Mentors XP</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          {wallet && (
            <OverviewTab
              wallet={{ ...wallet, config: walletData?.config }}
              badges={badges}
              converting={converting}
              onConvert={handleConvert}
            />
          )}
        </TabsContent>

        <TabsContent value="badges" className="mt-6">
          <BadgesTab badges={badges} />
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-6">
          <LeaderboardTab leaderboard={lbData.leaderboard} myEntry={lbData.myEntry} />
        </TabsContent>

        <TabsContent value="milestones" className="mt-6">
          <MilestonesTab milestones={milestones} />
        </TabsContent>

        <TabsContent value="mentors" className="mt-6">
          <MentorsTab mentors={mentorData.mentors} totalMnxp={mentorData.totalMnxp} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
